import { makeAutoObservable, runInAction } from 'mobx';
import { agent } from '../api/agent';
import { toast } from 'react-toastify';
import i18n from '../../i18n';
import { CreateTeamForm, InvitableUserDto, TeamDto, TeamInvitationDto, TeamLinkDto } from '../models/Team';

export default class TeamStore {
  selectedTeam: TeamDto | undefined = undefined;
  myTeams: TeamDto[] = [];
  myInvitations: TeamInvitationDto[] = [];
  invitableUsers: InvitableUserDto[] = [];

  loading = false;
  loadingInitial = false;
  submitting = false;
  searchingUsers = false;

  constructor() {
    makeAutoObservable(this);
  }

  // ── My Teams ──────────────────────────────────────────────────────────

  loadMyTeams = async () => {
    this.loading = true;
    try {
      const teams = await agent.Teams.getMyTeams();
      runInAction(() => {
        this.myTeams = teams;
        this.loading = false;
      });
    } catch (error) {
      console.error(error);
      runInAction(() => { this.loading = false; });
    }
  };

  // ── Team Details ─────────────────────────────────────────────────────

  loadTeam = async (id: string) => {
    runInAction(() => {
      this.loadingInitial = true;
      this.selectedTeam = undefined;
    });
    try {
      const team = await agent.Teams.getTeam(id);
      runInAction(() => {
        this.selectedTeam = team;
        this.loadingInitial = false;
      });
      return team;
    } catch (error) {
      console.error(error);
      runInAction(() => { this.loadingInitial = false; });
    }
  };

  clearSelectedTeam = () => { this.selectedTeam = undefined; };

  // ── Create / Edit ─────────────────────────────────────────────────────

  createTeam = async (data: CreateTeamForm): Promise<string | null> => {
    this.submitting = true;
    try {
      const result = await agent.Teams.createTeam({
        name: data.name,
        sportID: data.sportID,
        description: data.description,
        adminPhone: data.adminPhone,
        latitude: data.latitude,
        longitude: data.longitude,
      });
      runInAction(() => { this.submitting = false; });
      toast.success((i18n as any).t('teams.success.teamCreated'));
      await this.loadMyTeams();
      return result.id;
    } catch (error) {
      console.error(error);
      runInAction(() => { this.submitting = false; });
      toast.error((i18n as any).t('teams.errors.createFailed'));
      return null;
    }
  };

  updateTeam = async (id: string, data: { name?: string; description?: string; latitude?: number; longitude?: number ;adminPhone?: string }) => {
    this.submitting = true;
    try {
      await agent.Teams.updateTeam(id, data);
      runInAction(() => { this.submitting = false; });
      toast.success((i18n as any).t('teams.success.teamUpdated'));
      return true;
    } catch (error) {
      console.error(error);
      runInAction(() => { this.submitting = false; });
      toast.error((i18n as any).t('teams.errors.updateFailed'));
      return false;
    }
  };

  updateTeamStatus = async (id: string, status: string) => {
    this.submitting = true;
    try {
      await agent.Teams.updateStatus(id, status);
      runInAction(() => {
        this.submitting = false;
        this.myTeams = this.myTeams.map(t =>
          t.id === id ? { ...t, status: status as any } : t
        );
        if (this.selectedTeam?.id === id) {
          this.selectedTeam = { ...this.selectedTeam, status: status as any };
        }
      });
      toast.success((i18n as any).t('teams.success.statusUpdated'));
      return true;
    } catch (error) {
      console.error(error);
      runInAction(() => { this.submitting = false; });
      return false;
    }
  };

  uploadLogo = async (id: string, file: File): Promise<string | null> => {
    this.submitting = true;
    try {
      const result = await agent.Teams.uploadLogo(id, file);
      runInAction(() => {
        this.submitting = false;
        if (this.selectedTeam?.id === id) {
          this.selectedTeam = { ...this.selectedTeam, logoUrl: result.logoUrl };
        }
      });
      toast.success((i18n as any).t('teams.success.logoUploaded'));
      return result.logoUrl;
    } catch (error) {
      console.error(error);
      runInAction(() => { this.submitting = false; });
      toast.error((i18n as any).t('teams.errors.logoUploadFailed'));
      return null;
    }
  };

  // ── Members ───────────────────────────────────────────────────────────

  searchUsersToInvite = async (teamId: string, q: string) => {
    if (!q || q.length < 2) {
      runInAction(() => { this.invitableUsers = []; });
      return;
    }
    this.searchingUsers = true;
    try {
      const users = await agent.Teams.searchUsersToInvite(teamId, q);
      runInAction(() => {
        this.invitableUsers = users;
        this.searchingUsers = false;
      });
    } catch (error) {
      console.error(error);
      runInAction(() => { this.searchingUsers = false; });
    }
  };

  inviteMember = async (teamId: string, userId: string): Promise<boolean> => {
    this.submitting = true;
    try {
      await agent.Teams.inviteMember(teamId, userId);
      runInAction(() => {
        this.submitting = false;
        const user = this.invitableUsers.find(u => u.userId === userId);
        if (user) user.isAlreadyInvited = true;
      });
      toast.success((i18n as any).t('teams.success.invitationSent'));
      return true;
    } catch (error) {
      console.error(error);
      runInAction(() => { this.submitting = false; });
      toast.error((i18n as any).t('teams.errors.inviteFailed'));
      return false;
    }
  };

  removeMember = async (teamId: string, userId: string): Promise<boolean> => {
    this.submitting = true;
    try {
      await agent.Teams.removeMember(teamId, userId);
      runInAction(() => {
        this.submitting = false;
        if (this.selectedTeam) {
          this.selectedTeam = {
            ...this.selectedTeam,
            teamMembers: this.selectedTeam.teamMembers.filter(m => m.userId !== userId)
          };
        }
      });
      toast.success((i18n as any).t('teams.success.memberRemoved'));
      return true;
    } catch (error) {
      console.error(error);
      runInAction(() => { this.submitting = false; });
      toast.error((i18n as any).t('teams.errors.removeMemberFailed'));
      return false;
    }
  };

  promoteMember = async (teamId: string, userId: string): Promise<boolean> => {
    this.submitting = true;
    try {
      await agent.Teams.promoteMember(teamId, userId);
      runInAction(() => {
        this.submitting = false;
        if (this.selectedTeam) {
          this.selectedTeam = {
            ...this.selectedTeam,
            teamMembers: this.selectedTeam.teamMembers.map(m =>
              m.userId === userId ? { ...m, role: 'Captain' } : m
            )
          };
        }
      });
      toast.success((i18n as any).t('teams.success.memberPromoted'));
      return true;
    } catch (error) {
      console.error(error);
      runInAction(() => { this.submitting = false; });
      return false;
    }
  };

  leaveTeam = async (teamId: string): Promise<boolean> => {
    this.submitting = true;
    try {
      await agent.Teams.leaveTeam(teamId);
      runInAction(() => {
        this.submitting = false;
        this.myTeams = this.myTeams.filter(t => t.id !== teamId);
        if (this.selectedTeam?.id === teamId) this.selectedTeam = undefined;
      });
      toast.success((i18n as any).t('teams.success.leftTeam'));
      return true;
    } catch (error: any) {
      runInAction(() => { this.submitting = false; });
      toast.error(error?.response?.data || (i18n as any).t('teams.errors.leaveFailed'));
      return false;
    }
  };

  // ── Invitations ───────────────────────────────────────────────────────

  loadMyInvitations = async () => {
    this.loading = true;
    try {
      const invitations = await agent.Teams.getMyInvitations();
      runInAction(() => {
        this.myInvitations = invitations;
        this.loading = false;
      });
    } catch (error) {
      console.error(error);
      runInAction(() => { this.loading = false; });
    }
  };

  respondToInvitation = async (id: string, accept: boolean): Promise<boolean> => {
    this.submitting = true;
    try {
      await agent.Teams.respondToInvitation(id, accept);
      runInAction(() => {
        this.submitting = false;
        this.myInvitations = this.myInvitations.filter(inv => inv.id !== id);
      });
      toast.success(accept
        ? (i18n as any).t('teams.success.invitationAccepted')
        : (i18n as any).t('teams.success.invitationDeclined'));
      if (accept) {
        await this.loadMyTeams();
        if (this.selectedTeam) {
          await this.loadTeam(this.selectedTeam.id);
        }
      }
      return true;
    } catch (error) {
      console.error(error);
      runInAction(() => { this.submitting = false; });
      return false;
    }
  };

  // ── Links ─────────────────────────────────────────────────────────────

  updateLinks = async (teamId: string, links: TeamLinkDto[]): Promise<boolean> => {
    this.submitting = true;
    try {
      await agent.Teams.updateLinks(teamId, links);
      runInAction(() => {
        this.submitting = false;
        if (this.selectedTeam?.id === teamId) {
          this.selectedTeam = { ...this.selectedTeam, teamLinks: links };
        }
      });
      toast.success((i18n as any).t('teams.success.linksUpdated'));
      return true;
    } catch (error) {
      console.error(error);
      runInAction(() => { this.submitting = false; });
      return false;
    }
  };

  // ── Visibility ────────────────────────────────────────────────────────

  updateVisibility = async (teamId: string, section: string, visibility: string): Promise<boolean> => {
    this.submitting = true;
    try {
      await agent.Teams.updateVisibility(teamId, section, visibility);
      runInAction(() => { this.submitting = false; });
      return true;
    } catch (error) {
      console.error(error);
      runInAction(() => { this.submitting = false; });
      return false;
    }
  };

  clearInvitableUsers = () => { this.invitableUsers = []; };
}